import * as React from "react"
import { type Transaction } from "@digits-graphql/frontend/graphql-bearer"
import colorHelper from "@digits-shared/helpers/colorHelper"
import moneyFlowHelper from "@digits-shared/helpers/moneyFlowHelper"
import colors from "@digits-shared/themes/colors"
import { BodyText } from "@digits-shared/themes/typography"
import { styled } from "styled-components"
import { isCreditNormalCategory, isDebitNormalCategory } from "src/shared/helpers/categoryHelper"

const Total = styled.div`
  display: inline-flex;
  padding: 4px 8px;
  justify-content: center;
  align-items: center;
  gap: 12px;
  border-radius: 8px;
  background: ${colorHelper.hexToRgba(colors.accentBlue, 0.1)};
  margin-right: 32px;
`

interface BulkTotalProps {
  transactions: Transaction[]
}

export const BulkTotal: React.FC<BulkTotalProps> = ({ transactions }) => {
  const total = React.useMemo(() => {
    const moneyFlows = transactions.map((transaction) => transaction.moneyFlow)
    const total = moneyFlowHelper.addValues(...moneyFlows)
    return moneyFlowHelper.currency(total)
  }, [transactions])

  // Only show the total if all transactions are either debit or credit normal categories
  const showTotal = React.useMemo(() => {
    const hasDebitNormal = transactions.some((t) => isDebitNormalCategory(t.displayCategory.type))
    const hasCreditNormal = transactions.some((t) => isCreditNormalCategory(t.displayCategory.type))
    return (hasCreditNormal || hasDebitNormal) && !(hasCreditNormal && hasDebitNormal)
  }, [transactions])

  if (!showTotal) return null

  return (
    <Total>
      <BodyText weight="heavy">Total:</BodyText>
      <BodyText>{total}</BodyText>
    </Total>
  )
}
